import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation, faXmark } from "@fortawesome/free-solid-svg-icons";
import styles from "../WorkoutAdminModal.module.css";
import { useToast } from "../ToastProvider";
import { checkMediaFileSize } from "./workoutAdminHelpers";
import {
  MUSCLE_GROUP_OPTIONS,
  EQUIPMENT_OPTIONS,
  MOVEMENT_TYPE_OPTIONS,
} from "../workoutOptions";

export default function ExerciseItemModal({
  editingExerciseItemId,
  exerciseItemFile,
  exerciseItemForm,
  exerciseItemModalRef,
  askRemoveExerciseMedia,
  handleSaveExerciseItem,
  resetExerciseItemEditor,
  saving,
  selectedCategory,
  setDemoExerciseItem,
  setExerciseItemFile,
  setExerciseItemForm,
  requireClassification = false,
}) {
  const toast = useToast();

  return createPortal(
                      <div className={styles.innerOverlay} onClick={(e) => { if (e.target === e.currentTarget) resetExerciseItemEditor(); }}>
                        <div className={`${styles.planModal} ${styles.planModalNarrow}`} ref={exerciseItemModalRef} role="dialog" aria-modal="true" aria-labelledby="wam-exercise-item-title">
                          <div className={styles.planModalHeader}>
                            <h3 id="wam-exercise-item-title">{editingExerciseItemId ? "Editar exercício" : "Novo exercício"}</h3>
                            <button type="button" className={styles.planModalClose} onClick={resetExerciseItemEditor} aria-label="Fechar"><FontAwesomeIcon icon={faXmark} /></button>
                          </div>

                      <form className={styles.wamForm} onSubmit={handleSaveExerciseItem}>
                        <div className={styles.wamField}>
                          <label>Categoria</label>
                          <input
                            type="text"
                            className={styles.wamFieldReadonly}
                            value={selectedCategory?.name || ""}
                            disabled
                          />
                        </div>

                        <div className={styles.wamField}>
                          <label>Nome</label>
                          <input
                            type="text"
                            value={exerciseItemForm.name}
                            onChange={(e) =>
                              setExerciseItemForm((prev) => ({ ...prev, name: e.target.value }))
                            }
                            placeholder="Ex: Supino reto com barra"
                            required
                          />
                        </div>

                        <div className={styles.wamFormGrid}>
                          <div className={styles.wamField}>
                            <label htmlFor="wam-exercise-muscle">Grupo muscular{requireClassification ? " *" : ""}</label>
  <select
  id="wam-exercise-muscle"
  required={requireClassification}
  aria-required={requireClassification}
  value={exerciseItemForm.muscle_group}
  onChange={(e) =>
    setExerciseItemForm((prev) => ({
      ...prev,
      muscle_group: e.target.value,
    }))
  }
  >
  <option value="">Selecione</option>

  {[...MUSCLE_GROUP_OPTIONS]
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
                          </div>

                          <div className={styles.wamField}>
                            <label htmlFor="wam-exercise-equipment">Equipamento{requireClassification ? " *" : ""}</label>
  <select
  id="wam-exercise-equipment"
  required={requireClassification}
  aria-required={requireClassification}
  value={exerciseItemForm.equipment}
  onChange={(e) =>
    setExerciseItemForm((prev) => ({
      ...prev,
      equipment: e.target.value,
    }))
  }
  >
  <option value="">Selecione</option>

  {[...EQUIPMENT_OPTIONS]
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
                          </div>
                        </div>
                        {requireClassification && (
                          <p className={styles.sectionHint} style={{ margin: 0 }}>
                            * Obrigatórios: a geração de treino por IA usa o grupo muscular e o equipamento pra escolher os seus exercícios.
                          </p>
                        )}

                        <div className={styles.wamField}>
                          <label>Tipo de movimento</label>
                          <select
                            value={exerciseItemForm.movement_type}
                            onChange={(e) =>
                              setExerciseItemForm((prev) => ({
                                ...prev,
                                movement_type: e.target.value,
                              }))
                            }
                          >
                            <option value="">Selecione</option>
                            {MOVEMENT_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className={styles.wamField}>
                          <label>Observações</label>
                          <textarea
                            rows="1"
                            value={exerciseItemForm.notes}
                            onChange={(e) =>
                              setExerciseItemForm((prev) => ({ ...prev, notes: e.target.value }))
                            }
                            placeholder="Observações e dicas"
                          />
                        </div>

                        <div className={styles.wamField}>
                          <label>URL da demonstração (YouTube/Shorts, GIF/MP4/WEBM ou imagem)</label>
                          <input
                            type="text"
                            value={exerciseItemForm.demo_url}
                            onChange={(e) =>
                              setExerciseItemForm((prev) => ({ ...prev, demo_url: e.target.value }))
                            }
                            placeholder="Ex.: https://youtube.com/shorts/ID, uploads/media/exercicio.gif, ou https://..."
                          />
                        </div>

                        <div className={styles.wamField}>
                          <label>Mídia de demonstração (upload) - prioridade para URL quando ambas preenchidas</label>
                          <input
                            type="file"
                            accept="image/*,video/mp4,video/webm,video/quicktime,.mov,.gif,.svg"
                            onChange={(e) => {
                              const f = e.target.files?.[0] || null;
                              if (f) {
                                const sizeError = checkMediaFileSize(f);
                                if (sizeError) {
                                  toast.error(sizeError);
                                  e.target.value = "";
                                  return;
                                }
                              }
                              setExerciseItemFile(f);
                            }}
                          />
                          {(() => {
                            const f = exerciseItemFile;
                            if (!f) return null;
                            const ext = (f.name?.split('.').pop() || '').toLowerCase();
                            const type = f.type || '';
                            const isGif = ext === 'gif' || type === 'image/gif';
                            const isVideo = type.startsWith('video/') || ['mp4','webm','mov'].includes(ext);
                            const kind = isGif ? 'gif' : isVideo ? 'video' : 'image';
                            const slug = (exerciseItemForm.name || '')
                              .normalize('NFD').replace(/\p{Diacritic}/gu,'')
                              .toLowerCase().trim().replace(/[^a-z0-9]+/g,'_').replace(/^_|_$/g,'') || 'media';
                            const preview = `uploads/media/${slug}_${kind}.${ext || 'file'}`;
                            return (
                              <small>
                                Arquivo selecionado: {f.name} - será salvo como <strong>{preview}</strong>
                              </small>
                            );
                          })()}
                          {exerciseItemForm.demo_url?.startsWith('uploads/media') && !exerciseItemFile && (
                            <small>Arquivo vinculado atual: {exerciseItemForm.demo_url}</small>
                          )}
                        </div>

                        <label className={styles.wamCheck}>
                          <input
                            type="checkbox"
                            checked={exerciseItemForm.is_active}
                            onChange={(e) =>
                              setExerciseItemForm((prev) => ({
                                ...prev,
                                is_active: e.target.checked,
                              }))
                            }
                          />
                          <span>Exercício ativo</span>
                        </label>

                        {exerciseItemForm.demo_status === "failed" && (
                          // Vídeo continua tocável (fallback pro arquivo bruto, ver
                          // ExerciseDemoModal.jsx) — aviso não-bloqueante, mesmo padrão
                          // visual âmbar usado nos outros avisos deste modal.
                          <div style={{
                            background: "rgba(251,191,36,0.08)",
                            border: "1px solid rgba(251,191,36,0.22)",
                            borderRadius: 8,
                            padding: "10px 13px",
                            display: "flex",
                            gap: 8,
                            alignItems: "flex-start",
                          }}>
                            <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: "var(--feedback-warning)", fontSize: 13, marginTop: 2, flexShrink: 0 }} />
                            <p style={{ color: "var(--text-0-70)", fontSize: 12, margin: 0, lineHeight: 1.55 }}>
                              O vídeo enviado não pôde ser otimizado automaticamente — continua funcionando normalmente, mas considere reenviar um arquivo menor ou mais curto.
                            </p>
                          </div>
                        )}

                        <div className={styles.actionsRow}>
                          {editingExerciseItemId && exerciseItemForm.demo_url && (
                            <>
                              <button
                                type="button"
                                className={`${styles.wamBtn} ${styles.ghost}`}
                                onClick={() => setDemoExerciseItem({ name: exerciseItemForm.name, demo_url: exerciseItemForm.demo_url })}
                              >
                                ▶ Demo
                              </button>
                              <button
                                type="button"
                                className={`${styles.wamBtn} ${styles.danger}`}
                                onClick={askRemoveExerciseMedia}
                                disabled={saving}
                              >
                                Remover mídia
                              </button>
                            </>
                          )}
                          <button
                            type="submit"
                            className={`${styles.wamBtn} ${styles.primary}`}
                            disabled={saving}
                          >
                            {editingExerciseItemId ? "Salvar alterações" : "Criar exercício"}
                          </button>
                        </div>
                      </form>
                        </div>
                      </div>,
                      document.body
                    )
}
